import { useEffect, useMemo } from 'react';
import {
  Box, Flex, VStack, HStack, Text, Button, Image, Badge, Link, useToast
} from '@chakra-ui/react';
import { chromeOptionsForRatio, CHROME_LABEL } from '../../components/adChrome';
import { funnelStageLabel, mediaTypeLabel } from '../../components/adLabels';
import type { ShowcaseAd, ShowcaseCfg } from './types';
import { resolveAdCopy, resolveShowcaseChrome, ShowcaseAdMedia, ShowcaseChromeFrame } from './chrome';
import { safeHttpUrl } from '../../lib/safeHref';

const MODAL_W: Record<ShowcaseAd['ratio'], string> = {
  '9:16': '372px', '4:5': '452px', '1:1': '496px', '1.91:1': '760px', '16:9': '760px'
};
const THUMB: Record<ShowcaseAd['ratio'], [string, string]> = {
  '9:16': ['13px', '23px'], '4:5': ['18px', '23px'], '1:1': ['22px', '22px'],
  '1.91:1': ['30px', '16px'], '16:9': ['30px', '17px']
};

type Props = {
  ad: ShowcaseAd;
  ads: ShowcaseAd[];          // current filtered + ordered list (prev/next)
  allAds: ShowcaseAd[];       // unfiltered, for ratio-variant siblings
  cfg: ShowcaseCfg;
  overrides: Record<string, string>;
  surround: 'dark' | 'light';
  railOpen: boolean;
  onRailChange: (open: boolean) => void;
  onClose: () => void;
  onSelect: (adId: string) => void;
  onOverrideChange?: (adId: string, kind: string) => void;
};

export function AdShowcaseModal({
  ad, ads, allAds, cfg, overrides, surround, railOpen,
  onRailChange, onClose, onSelect, onOverrideChange
}: Props) {
  const toast = useToast();
  const dark = surround === 'dark';
  const panel = dark ? '#12141A' : 'white';
  const panel2 = dark ? '#171A21' : 'gray.50';
  const fg = dark ? '#F5F7FA' : 'brand.ink';
  const fg2 = dark ? '#A8B0BE' : 'brand.muted';
  const fg3 = dark ? '#6C7484' : 'gray.500';
  const bd = dark ? 'whiteAlpha.200' : 'brand.border';

  const idx = ads.findIndex(a => a.id === ad.id);
  const chrome = resolveShowcaseChrome(ad, overrides);
  const copy = resolveAdCopy(ad, cfg);
  const options = chromeOptionsForRatio(ad.ratio, ad.family);
  const stage = funnelStageLabel(ad.stage);
  const productHref = safeHttpUrl(ad.product.productUrl);

  const variants = useMemo(() => {
    if (!ad.concept) return [ad];
    const sibs = allAds.filter(a => a.concept === ad.concept);
    return sibs.length ? sibs : [ad];
  }, [ad, allAds]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      if (ads.length === 0) return;
      const dir = e.key === 'ArrowRight' ? 1 : -1;
      const i = ads.findIndex(a => a.id === ad.id);
      const from = i < 0 ? 0 : i;
      const nx = (from + dir + ads.length) % ads.length;
      onSelect(ads[nx].id);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [ad.id, ads, onClose, onSelect]);

  function step(dir: number) {
    if (ads.length === 0) return;
    const i = ads.findIndex(a => a.id === ad.id);
    const from = i < 0 ? 0 : i;
    const nx = (from + dir + ads.length) % ads.length;
    onSelect(ads[nx].id);
  }

  const isMeta = ad.family === 'meta';
  const placementNote = isMeta
    ? 'Meta placements are bought directly, so this ad only runs where the format targets.'
    : 'Performance Max hands one asset to Google, which chooses the surface. Every placement here is a real home for this creative.';

  return (
    <Flex
      position="fixed"
      inset={0}
      zIndex={1400}
      align="center"
      justify="center"
      p={7}
      bg="rgba(4,6,10,0.8)"
      backdropFilter="blur(14px)"
      onClick={onClose}
    >
      <Flex
        position="relative"
        maxW="1180px"
        maxH="calc(100vh - 56px)"
        bg={panel}
        borderWidth="1px"
        borderColor={bd}
        borderRadius="18px"
        overflow="hidden"
        boxShadow="0 40px 90px rgba(0,0,0,.55)"
        onClick={e => e.stopPropagation()}
        color={fg}
      >
        <Flex
          flex={1}
          minW={0}
          direction="column"
          align="center"
          gap={4}
          px={10}
          py={8}
          overflowY="auto"
          bg={panel2}
        >
          <Box w={MODAL_W[ad.ratio]} maxW="100%" borderRadius="10px" overflow="hidden" boxShadow="0 24px 60px rgba(0,0,0,.4)">
            <ShowcaseChromeFrame ad={ad} cfg={cfg} overrides={overrides} density="full">
              <ShowcaseAdMedia ad={ad} mode="modal" />
            </ShowcaseChromeFrame>
          </Box>
          <HStack spacing={2.5} fontSize="11.5px" color={fg3}>
            <Text>{CHROME_LABEL[chrome]}</Text>
            <Text>·</Text>
            <Text>{ad.ratio}</Text>
            <Text>·</Text>
            <Text fontFamily="mono">{ad.platformFormat}</Text>
            <Text>·</Text>
            <Text fontFamily="mono">{ad.id}</Text>
            <Button
              size="xs" variant="ghost" minW="auto" h="auto" py={0} px={1}
              onClick={() => void navigator.clipboard.writeText(ad.id).then(
                () => toast({ title: 'Ad ID copied', status: 'success', duration: 2000 }),
                () => toast({ title: 'Copy failed', description: ad.id, status: 'error', duration: 4000 })
              )}
            >
              Copy
            </Button>
          </HStack>
        </Flex>

        {railOpen ? (
          <Flex w="336px" flex="none" direction="column" overflowY="auto" borderLeftWidth="1px" borderColor={bd}>
            <HStack spacing={2.5} px={5} py={4} borderBottomWidth="1px" borderColor={bd}>
              <Badge
                fontSize="10.5px"
                letterSpacing="0.1em"
                textTransform="uppercase"
                color={isMeta ? '#4DA8E8' : '#A98BF5'}
                bg={isMeta ? 'rgba(11,132,216,.16)' : 'rgba(122,53,232,.16)'}
                borderRadius="5px"
              >
                {isMeta ? 'Meta' : 'Google PMax'}
              </Badge>
              <Text fontSize="12px" color={fg2}>{mediaTypeLabel(ad.mediaKind)}</Text>
              <Button
                ml="auto"
                size="xs"
                variant="outline"
                borderColor={bd}
                color={fg3}
                onClick={() => onRailChange(false)}
              >
                Focus
              </Button>
              <Button
                size="xs"
                variant="outline"
                borderColor={bd}
                color={fg2}
                minW="26px"
                h="26px"
                px={0}
                onClick={onClose}
                aria-label="Close"
              >
                ×
              </Button>
            </HStack>

            <VStack align="stretch" spacing={6} p={5} flex={1}>
              <Box>
                <Text fontSize="16px" fontWeight="600" letterSpacing="-0.02em" lineHeight="1.3">
                  {ad.product.name || 'Untitled product'}
                </Text>
                {stage && (
                  <Text mt={1} fontSize="12px" color={fg2}>{stage}</Text>
                )}
                {ad.product.productUrl && (
                  <Box mt={2.5}>
                    <Text fontSize="9px" fontWeight="800" textTransform="uppercase" letterSpacing="0.06em" color={fg3}>
                      Retailer product page
                    </Text>
                    {productHref ? (
                      <Link
                        href={productHref}
                        isExternal
                        fontSize="sm"
                        fontWeight="600"
                        color="rsViolet.500"
                        _hover={{ textDecoration: 'underline' }}
                      >
                        View on retailer's site ↗
                      </Link>
                    ) : (
                      <Text fontSize="sm" fontWeight="600" color={fg2}>
                        View on retailer's site ↗
                      </Text>
                    )}
                  </Box>
                )}
              </Box>

              <Box>
                <Text fontSize="10.5px" fontWeight="700" letterSpacing="0.11em" textTransform="uppercase" color={fg3} mb={2.5}>
                  Placement
                </Text>
                <Flex wrap="wrap" gap={2}>
                  {options.map(opt => {
                    const on = chrome === opt.kind;
                    return (
                      <Button
                        key={opt.kind}
                        size="xs"
                        variant={on ? 'solid' : 'outline'}
                        bg={on ? 'rsViolet.500' : panel2}
                        color={on ? 'white' : fg2}
                        borderColor={on ? 'rsViolet.500' : bd}
                        fontWeight="600"
                        isDisabled={!onOverrideChange}
                        onClick={() => onOverrideChange?.(ad.id, opt.kind)}
                      >
                        {opt.label}
                      </Button>
                    );
                  })}
                </Flex>
                <Text mt={2} fontSize="11px" color={fg3} lineHeight="1.5">{placementNote}</Text>
              </Box>

              {variants.length > 1 && (
                <Box>
                  <Text fontSize="10.5px" fontWeight="700" letterSpacing="0.11em" textTransform="uppercase" color={fg3} mb={2.5}>
                    Ratio variants
                  </Text>
                  <Flex gap={2} wrap="wrap">
                    {variants.map(v => {
                      const on = v.id === ad.id;
                      const [tw, th] = THUMB[v.ratio];
                      return (
                        <Button
                          key={v.id}
                          variant="outline"
                          borderColor={on ? 'rsViolet.500' : bd}
                          bg={panel2}
                          h="auto"
                          w="60px"
                          px={1.5}
                          py={1.5}
                          flexDirection="column"
                          gap={1.5}
                          onClick={() => onSelect(v.id)}
                        >
                          {v.posterUrl || (v.mediaKind === 'image' && v.mediaUrl) ? (
                            <Image
                              src={v.posterUrl || v.mediaUrl}
                              alt=""
                              w={tw}
                              h={th}
                              borderRadius="3px"
                              objectFit="cover"
                            />
                          ) : (
                            <Box w={tw} h={th} borderRadius="3px" bg="gray.700" borderWidth="1px" borderColor={bd} />
                          )}
                          <Text fontSize="10px" fontWeight="600" color={on ? fg : fg3}>{v.ratio}</Text>
                        </Button>
                      );
                    })}
                  </Flex>
                </Box>
              )}

              <Box>
                <Text fontSize="10.5px" fontWeight="700" letterSpacing="0.11em" textTransform="uppercase" color={fg3} mb={2.5}>
                  Ad copy
                </Text>
                <VStack align="stretch" spacing={3}>
                  <CopyRow label="Headline" value={copy.headline} fg={fg} fg3={fg3} />
                  <CopyRow label="Primary text" value={copy.primary} fg={fg} fg3={fg3} />
                  <CopyRow label="Description" value={copy.description} fg={fg} fg3={fg3} />
                  <CopyRow label="CTA" value={copy.cta} fg={fg} fg3={fg3} />
                  <CopyRow label="Destination" value={copy.destination} fg={fg} fg3={fg3} />
                </VStack>
              </Box>
            </VStack>

            <HStack spacing={2} px={5} py={4} mt="auto" borderTopWidth="1px" borderColor={bd}>
              <Button flex={1} size="sm" variant="outline" borderColor={bd} color={fg2} onClick={() => step(-1)}>
                ← Prev
              </Button>
              <Text fontSize="11px" color={fg3} sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {idx >= 0 ? `${idx + 1} / ${ads.length}` : `– / ${ads.length}`}
              </Text>
              <Button flex={1} size="sm" variant="outline" borderColor={bd} color={fg2} onClick={() => step(1)}>
                Next →
              </Button>
            </HStack>
          </Flex>
        ) : (
          <HStack position="absolute" top={0} right={0} p={5} spacing={2}>
            <Button
              size="xs"
              bg="blackAlpha.600"
              color="white"
              borderWidth="1px"
              borderColor={bd}
              _hover={{ bg: 'blackAlpha.700' }}
              onClick={() => onRailChange(true)}
            >
              Details
            </Button>
          </HStack>
        )}
      </Flex>
    </Flex>
  );
}

function CopyRow({ label, value, fg, fg3 }: { label: string; value: string; fg: string; fg3: string }) {
  return (
    <Box>
      <Text fontSize="10.5px" color={fg3} mb="3px">{label}</Text>
      <Text fontSize="12.5px" lineHeight="1.5" color={fg}>{value || '—'}</Text>
    </Box>
  );
}
